/**
 * emisFileNaming.js
 * -----------------
 * The EMIS file-name convention (TDI-390), mirrored from
 * python-functions/rms_functions/rms_emis_naming.py — keep in sync; the shared
 * fixture (python-functions/tests/fixtures/emis_filename_cases.json) pins the
 * two builders to each other.
 *
 *   {YY-MM-DD} {Child's name} {Document} {School} {Referrer's name} {Referrer's role}.pdf
 *
 * The backend names every download with its copy; this mirror exists so the
 * Admin Handover screen can show and copy a file's name BEFORE the download
 * is requested (TDI-388). Dates are year-first with hyphens; slashes never
 * appear; missing parts are omitted, never written as placeholder text.
 */

/* eslint-disable no-control-regex -- the filename-illegal set includes control characters by design */
const ILLEGAL = /[\\/:*?"<>|\u0000-\u001f]/g;

/** The {Document} labels the spec fixes; per-form labels are agreed on TDI-388. */
export const DOCUMENT_LABEL_LETTER = 'ROL';
export const DOCUMENT_LABEL_EVIDENCE_PACK = 'Evidence Pack';

/** Respondent words as they appear in file names: no slashes. */
export const FILE_ROLE_LABELS = { guardian: 'Parent-carer', school_user: 'School' };

/**
 * The {Document} label for one form's own PDF (TDI-388: the ADHD service
 * files each form separately — Tom King, 26 October 2026; labels approved as
 * the working set, changeable later). Fixed configuration, never the live
 * schema title, so the name shown always equals the name the download
 * arrives under. SNAP-IV is special-cased ('SNAP-IV {role label}').
 * Mirrors EMIS_FORM_LABELS in rms_functions/rms_emis_naming.py — keep in sync.
 */
export const EMIS_FORM_LABELS = {
  requestForReferral: 'Request for Referral',
  reasonForReferral: 'Reason for Referral',
  educationHistory: 'Education History',
  theirEarlyLife: 'Their Early Life',
  theirFamilyAndHomeLife: 'Their Family and Home Life',
  theirLifestyle: 'Their Lifestyle',
  theirHistoryWithTheLaw: 'Their History with the Law',
  theirFamilyHistory: 'Their Family History',
  theirMedicalHistory: 'Their Medical History',
  theirMentalHealthHistory: 'Their Mental Health History',
};

/** One name component: illegal characters removed, whitespace collapsed,
 * spaces and accented letters kept; missing values become ''. */
export function cleanComponent(value) {
  return String(value == null ? '' : value).replace(ILLEGAL, ' ').split(/\s+/).filter(Boolean).join(' ');
}

/**
 * A date as the convention writes it: 'YY-MM-DD'. Takes a Firestore
 * Timestamp, a Date or an ISO date string; null rather than guessing.
 */
export function yyMmDd(value) {
  if (!value) return null;
  if (typeof value === 'string') {
    const m = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
    return m ? `${m[1].slice(2)}-${m[2]}-${m[3]}` : null;
  }
  const date = typeof value.toDate === 'function' ? value.toDate() : value;
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return null;
  const iso = date.toISOString();
  return `${iso.slice(2, 4)}-${iso.slice(5, 7)}-${iso.slice(8, 10)}`;
}

/**
 * The convention's file name from its parts; missing parts are omitted.
 * extension=null returns the bare stem.
 */
export function buildEmisFilename({
  dateValue,
  childName,
  documentLabel,
  schoolName = null,
  referrerName = null,
  referrerRole = null,
  extension = 'pdf',
} = {}) {
  const stem = [
    yyMmDd(dateValue),
    cleanComponent(childName),
    cleanComponent(documentLabel),
    cleanComponent(schoolName),
    cleanComponent(referrerName),
    cleanComponent(referrerRole),
  ].filter(Boolean).join(' ');
  return extension ? `${stem}.${extension}` : stem;
}

/** The referrer's role word ('SENCo'): roleTitle, else the part of
 * relationshipLabel before ' at '. Null rather than invented. */
export function referrerRoleLabel(patient) {
  const referrer = patient?.referrer || {};
  const label = String(referrer.roleTitle || referrer.relationshipLabel || '').trim();
  if (!label) return null;
  return label.split(' at ')[0].trim() || null;
}

/** The record-derived parts every EMIS file name shares. Mirrors
 * emis_name_parts in rms_emis_naming.py. */
export function emisNameParts(patient) {
  const d = patient || {};
  const subject = d.subject || {};
  const school = d.school || {};
  const childName = [
    String(subject.firstName || '').trim(),
    String(subject.lastName || '').trim(),
  ].filter(Boolean).join(' ') || null;
  return {
    childName,
    schoolName: school.schoolName || school.name || d.educationSetting || d.schoolName || null,
    referrerName: (d.referrer || {}).fullName || null,
    referrerRole: referrerRoleLabel(d),
  };
}

/** The date evidence files carry: the received date, falling back to the
 * referral's createdAt for records that predate the readiness gate. */
export function evidenceDate(patient) {
  const d = patient || {};
  return d.triageReadiness?.markedAt || d.createdAt || null;
}

/**
 * The {Document} label for one of the handover screen's document rows, from
 * the row's key (documentDownloads in referralDocumentExport.js). Mirrors the
 * labels rmsExportReferralDocument writes into the real download, so the name
 * the copy button offers is the name the file arrives under — keep in sync
 * with the per-kind labels in rms_referral_document_export.py. Unknown keys
 * return null: no label is better than a wrong one in a clinical record.
 */
export function emisDocumentLabelForRow(rowKey) {
  const [kind, qualifier] = String(rowKey || '').split(':');
  // The evidence pack (TDI-389): one PDF for the pathways that file a pack,
  // named by the export as '... Evidence Pack ...'.
  if (kind === 'evidencePack') return DOCUMENT_LABEL_EVIDENCE_PACK;
  if (kind === 'respondent' && FILE_ROLE_LABELS[qualifier]) {
    return `Questionnaires ${FILE_ROLE_LABELS[qualifier]}`;
  }
  if (kind === 'form' && qualifier && qualifier.startsWith('snapIV26_')) {
    const role = qualifier.slice('snapIV26_'.length);
    return FILE_ROLE_LABELS[role] ? `SNAP-IV ${FILE_ROLE_LABELS[role]}` : null;
  }
  if (kind === 'form' && qualifier) {
    // Per-form rows (TDI-388, separate-documents pathways): the fixed form
    // label plus the respondent word, exactly as the export names the file.
    const suffix = ['_school_user', '_guardian'].find((s) => qualifier.endsWith(s));
    if (suffix) {
      const formKey = qualifier.slice(0, -suffix.length);
      const role = suffix.slice(1);
      if (EMIS_FORM_LABELS[formKey] && FILE_ROLE_LABELS[role]) {
        return `${EMIS_FORM_LABELS[formKey]} ${FILE_ROLE_LABELS[role]}`;
      }
    }
  }
  if (kind === 'decision') {
    const pathwayWord = { adhd: 'ADHD', asd: 'Autism' }[qualifier];
    return pathwayWord ? `${pathwayWord} Triage Decision` : 'Triage Decision';
  }
  // Legacy unsuffixed SNAP instances (form:snapIV26) cannot say whose they
  // are from the key alone, so they get no name rather than a wrong one.
  return null;
}

/**
 * The full EMIS file name for one document row of a referral, or null when
 * the row's label is unknown. Evidence rows are dated by evidenceDate.
 * An uploaded document's row (TDI-612) passes its {Document} label, the
 * server-resolved emisLabel ("EHCP and assessment paperwork School"),
 * because it cannot be derived from the row key.
 */
export function emisFilenameForRow(patient, rowKey, documentLabelOverride = null) {
  // A single-concern referral's decision row is keyed 'decision:referral',
  // but the export names its file by the referral's one pathway ('Autism
  // Triage Decision'), so the name shown resolves the pathway the same way.
  let key = rowKey;
  const concern = String(patient?.intakeConcern || '').trim().toLowerCase();
  if (key === 'decision:referral' && (concern === 'adhd' || concern === 'asd')) {
    key = `decision:${concern}`;
  }
  const documentLabel = documentLabelOverride || emisDocumentLabelForRow(key);
  if (!documentLabel) return null;
  const parts = emisNameParts(patient);
  return buildEmisFilename({
    dateValue: evidenceDate(patient),
    childName: parts.childName,
    documentLabel,
    schoolName: parts.schoolName,
    referrerName: parts.referrerName,
    referrerRole: parts.referrerRole,
  });
}
